import { WEALTH_BAND_KIND, WEALTH_BAND_LABELS, WealthBand, type Analysis } from '@ni/shared';
import { analysisCaveats, analysisOfKind, analysisProse } from '../../lib/analysis-copy';
import { NET_WORTH_LABEL, NET_WORTH_NO_DATA } from '../../lib/copy';
import { Caveats } from '../ui/caveats';

/**
 * The subject's net worth estimate: a band, or `band: null` where research found
 * nothing to estimate from. Null altogether when no readable `wealth_band` is on
 * record.
 */
export function netWorthEstimate(
  analyses: Analysis[],
): { analysis: Analysis; band: WealthBand | null } | null {
  const analysis = analysisOfKind(analyses, WEALTH_BAND_KIND);
  if (!analysis) return null;
  const band = WealthBand.nullable().safeParse(analysis.content.band);
  return band.success ? { analysis, band: band.data } : null;
}

/** True when research placed the subject in a band, which alone opens Finances. */
export function hasNetWorthBand(analyses: Analysis[]): boolean {
  return Boolean(netWorthEstimate(analyses)?.band);
}

/**
 * **Estimated net worth**, at the head of Finances
 * (docs/notes/2026-10-08-net-worth-bands.md): the band and what it rests on, or
 * "No data" where the record carries nothing to estimate from. Labeled as an
 * estimate wherever it appears, and never shown as a figure.
 */
export function NetWorthBlock({ analyses }: { analyses: Analysis[] }) {
  const estimate = netWorthEstimate(analyses);
  if (!estimate) return null;
  const basis = analysisProse(estimate.analysis);
  return (
    <div className="net-worth">
      <p className="net-worth-band">
        <span className="k">{NET_WORTH_LABEL}</span>
        <span className="v">
          {estimate.band ? WEALTH_BAND_LABELS[estimate.band] : NET_WORTH_NO_DATA}
        </span>
      </p>
      {basis ? <p className="net-worth-basis">{basis}</p> : null}
      <Caveats caveats={analysisCaveats(estimate.analysis)} />
    </div>
  );
}
